import type { ReactNode } from 'react'

import { FilterChipBar } from './FilterChipBar'
import { type GisTab } from '../../api/types'
import { SegmentedControl } from '../ui/SegmentedControl'

interface PanelTabsProps {
  tab: GisTab
  onChange: (tab: GisTab) => void
  /** Applied-filter chips shown inline between the tabs and the right controls. Hidden when empty. */
  filters?: { chips: { label: string; clear: () => void }[]; onClearAll: () => void } | undefined
  /** Optional controls rendered on the right of the tab row (e.g. entity + date-range filters). */
  right?: ReactNode
}

const LABELS: Record<GisTab, string> = {
  overview: 'Dashboard', // the `overview` tab key IS the V5 Dashboard (key kept for deep-links/map plumbing)
  coverage: 'Coverage',
  issues: 'Issues',
  officers: 'Officers',
}

// Tab strip order — Dashboard (observation) first, then the Issues/Coverage working boards.
// Officers stays commented out: kept in the code/types (and reachable via `?tab=`) but hidden.
const VISIBLE_TABS: GisTab[] = ['overview', 'issues', 'coverage' /* , 'officers' */]

// Mock token: active Issues tab carries a red leading dot, all other tabs a muted-grey dot.
const TAB_DOT: Partial<Record<GisTab, string>> = { issues: 'var(--gis-red)' }
const DOT_INACTIVE = 'var(--gis-muted)'

export function PanelTabs({ tab, onChange, filters, right }: PanelTabsProps) {
  const items = VISIBLE_TABS.map((t) => ({
    key: t,
    label: LABELS[t],
    // Active state recolors the dot to the tab accent (red for Issues); inactive is always muted.
    dot: t === tab ? (TAB_DOT[t] ?? DOT_INACTIVE) : DOT_INACTIVE,
  }))
  return (
    <nav className="flex items-center justify-between gap-2 border-b border-gis-line bg-white px-3.5 py-1.5">
      <div className="flex flex-none overflow-x-auto">
        <SegmentedControl<GisTab>
          items={items}
          value={tab}
          onChange={onChange}
          size="sm"
          track="a"
          shape="pill"
        />
      </div>
      {filters && filters.chips.length > 0 && (
        <div className="flex min-w-0 flex-1 items-center">
          <FilterChipBar chips={filters.chips} onClearAll={filters.onClearAll} />
        </div>
      )}
      {right && <div className="flex flex-none items-center gap-[9px]">{right}</div>}
    </nav>
  )
}
